import axios from "./axios";

export interface Economy24hStats {
  totalPackagesSold: number;
  mostPopularPackageName: string;
  totalZTokensSpent: number;
  singleUnlockCount: number;
}

export interface Reading24hStats {
  totalReads: number;
  premiumReads: number;
  freePreviewReads: number;
}

export interface ProgressStats {
  averageUserLevel: number;
  top5UsersByLevel: Array<{ username: string; level: number }>;
  topAchievementName: string;
}

export interface QueueStats {
  pendingChaptersCount: number;
  chaptersPublishedInRange: number;
}

export interface RecentPurchaseItem {
  username: string;
  itemType: "package" | "single-chapter";
  amountZ: number;
  timestamp: string;
}

export interface QuotaOverviewStats {
  freeQuotaUsed: number;
  premiumQuotaUsed: number;
  usageEntries: number;
  activePackageUsers: number;
  totalUsers: number;
}

export type TopContentScope = "content" | "part";

export interface TopContentItem {
  id: string;
  title: string;
  subtitle?: string | null;
  scope: TopContentScope;
  readCount: number;
}

export interface LogSummaryStats {
  total: number;
  infoCount: number;
  warningCount: number;
  errorCount: number;
  topAreas: Array<{ area: string; count: number }>;
  latestCritical?: {
    id: string;
    occurredAtUtc: string;
    area: string;
    action: string;
    actorName?: string;
    statusCode?: number;
  };
}

export type StatRange = "24h" | "all";

export interface CreatorAccessViolationStats {
  total: number;
  range: StatRange;
  recent: Array<{
    id: string;
    occurredAtUtc: string;
    actorName?: string;
    actorIp?: string;
    path?: string;
  }>;
}

export interface ContentPerformancePoint {
  date: string;
  readCount: number;
  premiumReadCount: number;
  freePreviewCount: number;
  purchaseCount: number;
  revenue: number;
}

export interface ContentPerformanceSeries {
  contentId: string;
  title: string;
  data: ContentPerformancePoint[];
}

export interface ContentPerformanceStats {
  days: number;
  series: ContentPerformanceSeries[];
}

export interface ClientEventSummaryStats {
  hours: number;
  action?: string | null;
  sinceUtc: string;
  total: number;
  actions: Record<string, number>;
  adsense: {
    attempts: number;
    success: number;
    failed: number;
    successRate: number;
  };
  topSlots: Array<{ slot: string; count: number }>;
  variants: Array<{ variant: string; count: number }>;
  variantBreakdown: Array<{
    variant: string;
    attempts: number;
    success: number;
    failed: number;
    successRate: number;
  }>;
  hourly: Array<{
    hourUtc: string;
    total: number;
    attempts: number;
    success: number;
    failed: number;
  }>;
}

export interface SingleContentPerformanceStats {
  contentId: string;
  title: string;
  days: number;
  data: ContentPerformancePoint[];
}

export async function getAdminDashboardEconomyStats(range: StatRange = "24h"): Promise<Economy24hStats> {
  try {
    const res = await axios.get("/admin/stats/economy", { params: { range } });
    return res.data as Economy24hStats;
  } catch (err) {
    if (range === "24h") {
      try {
        const fallback = await axios.get("/admin/stats/economy-24h");
        return fallback.data as Economy24hStats;
      } catch {
        /* ignore fallback failure */
      }
    }
    console.error("getAdminDashboardEconomyStats error", err);
    return { totalPackagesSold: 0, mostPopularPackageName: "-", totalZTokensSpent: 0, singleUnlockCount: 0 };
  }
}

export async function getAdminDashboardReadingStats(range: StatRange = "24h"): Promise<Reading24hStats> {
  try {
    const res = await axios.get("/admin/stats/reading", { params: { range } });
    return res.data as Reading24hStats;
  } catch (err) {
    if (range === "24h") {
      try {
        const fallback = await axios.get("/admin/stats/reading-24h");
        return fallback.data as Reading24hStats;
      } catch {
        /* ignore */
      }
    }
    console.error("getAdminDashboardReadingStats error", err);
    return { totalReads: 0, premiumReads: 0, freePreviewReads: 0 };
  }
}

export async function getAdminDashboardProgressStats(): Promise<ProgressStats> {
  try {
    const res = await axios.get("/admin/stats/progress");
    return res.data as ProgressStats;
  } catch (err) {
    console.error("getAdminDashboardProgressStats error", err);
    return { averageUserLevel: 0, top5UsersByLevel: [], topAchievementName: "-" };
  }
}

export async function getAdminDashboardQueueStats(range: StatRange = "24h"): Promise<QueueStats> {
  try {
    const res = await axios.get("/admin/stats/queue", { params: { range } });
    return res.data as QueueStats;
  } catch (err) {
    console.error("getAdminDashboardQueueStats error", err);
    return { pendingChaptersCount: 0, chaptersPublishedInRange: 0 };
  }
}

export async function getAdminRecentPurchases(): Promise<RecentPurchaseItem[]> {
  try {
    const res = await axios.get("/admin/stats/recent-purchases");
    return Array.isArray(res.data) ? (res.data as RecentPurchaseItem[]) : [];
  } catch (err) {
    console.error("getAdminRecentPurchases error", err);
    return [];
  }
}

export async function getTopTokenBalances(): Promise<Array<{ username: string; balanceZ: number }>> {
  try {
    const res = await axios.get("/admin/stats/top-balances");
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    console.error("getTopTokenBalances error", err);
    return [];
  }
}

export async function getAdminQuotaOverview(): Promise<QuotaOverviewStats | null> {
  try {
    const res = await axios.get("/admin/stats/quota-overview");
    return res.data as QuotaOverviewStats;
  } catch (err) {
    console.error("getAdminQuotaOverview error", err);
    return null;
  }
}

export async function getAdminTopContent(range: StatRange = "24h", scope: TopContentScope = "content"): Promise<TopContentItem[]> {
  try {
    const params: Record<string, string | number> = { scope };
    if (range === "all") {
      params.range = "all";
    } else {
      params.hours = 24;
    }
    const res = await axios.get("/admin/stats/top-content", { params });
    return Array.isArray(res.data) ? (res.data as TopContentItem[]) : [];
  } catch (err) {
    console.error("getAdminTopContent error", err);
    return [];
  }
}

export async function getAdminLogSummary(range: StatRange = "24h"): Promise<LogSummaryStats | null> {
  try {
    const params = range === "all" ? { range: "all" } : { hours: 24 };
    const res = await axios.get("/admin/stats/log-summary", { params });
    return res.data as LogSummaryStats;
  } catch (err) {
    console.error("getAdminLogSummary error", err);
    return null;
  }
}

export async function getCreatorAccessViolations(range: StatRange = "24h"): Promise<CreatorAccessViolationStats | null> {
  try {
    const params = range === "all" ? { range: "all" } : { hours: 24 };
    const res = await axios.get("/admin/stats/creator-access-violations", { params });
    return res.data as CreatorAccessViolationStats;
  } catch (err) {
    console.error("getCreatorAccessViolations error", err);
    return null;
  }
}

export async function getContentPerformanceStats(days = 7, take = 5): Promise<ContentPerformanceStats | null> {
  try {
    const res = await axios.get("/admin/stats/content-performance", { params: { days, take } });
    return res.data as ContentPerformanceStats;
  } catch (err) {
    console.error("getContentPerformanceStats error", err);
    return null;
  }
}

export async function getSingleContentPerformanceStats(contentId: string, days = 14): Promise<SingleContentPerformanceStats | null> {
  if (!contentId) {
    return null;
  }

  try {
    const res = await axios.get(`/admin/stats/content/${contentId}/performance`, { params: { days } });
    return res.data as SingleContentPerformanceStats;
  } catch (err) {
    console.error("getSingleContentPerformanceStats error", err);
    return null;
  }
}

export async function getClientEventSummary(hours = 24, action?: string): Promise<ClientEventSummaryStats | null> {
  try {
    const params: Record<string, string | number> = { hours };
    if (action && action.trim().length > 0) {
      params.action = action.trim();
    }
    const res = await axios.get("/admin/diagnostics/client-events/summary", { params });
    return res.data as ClientEventSummaryStats;
  } catch (err) {
    console.error("getClientEventSummary error", err);
    return null;
  }
}

export async function exportClientEventsCsv(hours = 24, action?: string): Promise<Blob | null> {
  try {
    const params: Record<string, string | number> = { hours };
    if (action && action.trim().length > 0 && action !== "all") {
      params.action = action.trim();
    }
    const res = await axios.get("/admin/diagnostics/client-events/export", {
      params,
      responseType: "blob",
    });
    return res.data as Blob;
  } catch (err) {
    console.error("exportClientEventsCsv error", err);
    return null;
  }
}
